<template>
  <div
    class="rs-status"
    :class="statusClasses"
  >
    <RsIcon
      v-if="showIcon"
      weight="far"
      :icon="statusIcon"
    />
    <span
      class="rs-status__text text-truncate"
    >{{ statusText }}</span>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';

const variantType: Array<string> = [
  '',
  'requested',
  'resolved',
  'scheduled',
  'declined',
  'spam',
  'expired',
  'waiting',
  'contacted',
  'converted',
  'unsubscribed',
  'unlisted',
  'removed',
  'enabled',
  'disabled',
  'hidden',
  'published',
  'not-published',
  'completed',
  'no-answer',
  'canceled',
  'failed',
  'busy',
  'delivered',
  'received',
  'undelivered',
  'private',
  'Read',
  'Unread',
  'in-progress',
  'promo-scheduled',
  'active',
  'inactive',
  'sent',
  'ivr-answered',
  'ivr-timeout',
];

export default defineComponent({
  name: 'RsStatus',

  props: {
    variant: {
      type: String,
      validator: (prop: string) => (prop ? variantType.includes(prop) : true),
      default: 'enabled',
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    statusText(): string {
      switch (this.variant) {
        case 'requested':
          return this.$ts('status.requested');
        case 'resolved':
          return this.$ts('status.resolved');
        case 'scheduled':
          return this.$ts('status.workScheduled');
        case 'unsubscribed':
          return 'Unsubscribed';
        case 'unlisted':
          return 'Unlisted';
        case 'sent':
          return 'Sent';
        case 'declined':
          return this.$ts('status.declined');
        case 'spam':
          return this.$ts('status.spam');
        case 'expired':
          return this.$ts('status.expired');
        case 'waiting':
          return 'Waiting';
        case 'contacted':
          return 'Contacted';
        case 'converted':
          return 'Converted';
        case 'enabled':
          return this.$ts('status.enabled');
        case 'disabled':
          return this.$ts('status.disabled');
        case 'hidden':
          return this.$ts('status.hidden');
        case 'removed':
          return 'Removed';
        case 'published':
          return this.$ts('status.published');
        case 'not-published':
          return this.$ts('status.notPublished');
        case 'completed':
          return this.$ts('status.completed');
        case 'no-answer':
          return this.$ts('status.noAnswer');
        case 'canceled':
          return this.$ts('status.canceled');
        case 'failed':
          return this.$ts('status.failed');
        case 'busy':
          return this.$ts('status.busy');
        case 'delivered':
          return this.$ts('status.delivered');
        case 'received':
          return this.$ts('status.received');
        case 'undelivered':
          return this.$ts('status.undelivered');
        case 'private':
          return this.$ts('status.notPublished');
        case 'Read':
          return 'N/A';
        case 'active':
          return this.$ts('status.active');
        case 'inactive':
          return this.$ts('status.inactive');
        case 'Unread':
          return 'N/A';
        case 'in-progress':
          return this.$ts('status.inProgress');
        case 'promo-scheduled':
          return this.$ts('status.scheduled');
        case 'ivr-answered':
          return this.$ts('status.ivrAnswered');
        case 'ivr-timeout':
          return this.$ts('status.ivrTimeout');
        default: return 'N/A';
      }
    },
    statusIcon(): string {
      switch (this.variant) {
        case 'requested':
          return 'info-circle';
        case 'resolved':
          return 'check-circle';
        case 'scheduled':
          return 'calendar-alt';
        case 'unsubscribed':
          return 'user-times';
        case 'sent':
          return 'share';
        case 'unlisted':
          return 'user-slash';
        case 'declined':
          return 'times-circle';
        case 'spam':
          return 'exclamation-triangle';
        case 'expired':
          return 'exclamation-triangle';
        case 'waiting':
          return 'hourglass';
        case 'contacted':
          return 'reply';
        case 'converted':
          return 'repeat';
        case 'removed':
          return 'minus-circle';
        case 'enabled':
          return 'check-circle';
        case 'published':
          return 'check-circle';
        case 'not-published':
          return 'times-circle';
        case 'private':
          return 'times-circle';
        case 'disabled':
          return 'times-circle';
        case 'hidden':
          return 'eye';
        case 'completed':
          return 'phone';
        case 'no-answer':
          return 'phone-slash';
        case 'canceled':
          return 'phone-slash';
        case 'failed':
          return 'times-circle';
        case 'busy':
          return 'Busy';
        case 'delivered':
          return 'check-circle';
        case 'received':
          return 'check-circle';
        case 'undelivered':
          return 'times-circle';
        case 'active':
          return 'check-circle';
        case 'inactive':
          return 'times-circle';
        case 'Read':
          return 'envelope-open';
        case 'Unread':
          return 'envelope';
        case 'in-progress':
          return 'check-circle';
        case 'promo-scheduled':
          return 'calendar-alt';
        case 'ivr-answered':
          return 'phone-slash';
        case 'ivr-timeout':
          return 'phone-slash';
        default:
          return '';
      }
    },
    statusClasses(): Record<string, boolean> {
      return {
        'rs-status--requested': this.variant === 'requested',
        'rs-status--unsubscribed': this.variant === 'unsubscribed',
        'rs-status--unlisted': this.variant === 'unlisted',
        'rs-status--sent': this.variant === 'sent',
        'rs-status--resolved': this.variant === 'resolved',
        'rs-status--scheduled': this.variant === 'scheduled',
        'rs-status--declined': ['declined', 'canceled'].includes(this.variant),
        'rs-status--spam': this.variant === 'spam',
        'rs-status--expired': this.variant === 'expired',
        'rs-status--waiting': ['waiting', 'busy', 'no-answer'].includes(this.variant),
        'rs-status--contacted': this.variant === 'contacted',
        'rs-status--converted': this.variant === 'converted',
        'rs-status--removed': ['removed', 'undelivered', 'failed'].includes(this.variant),
        'rs-status--enabled': ['published', 'enabled', 'completed', 'delivered', 'received', 'active'].includes(this.variant),
        'rs-status--published': this.variant === 'published',
        'rs-status--disabled': ['disabled', 'not-published', 'inactive'].includes(this.variant),
        'rs-status--Read': this.variant === 'Read',
        'rs-status--Unread': this.variant === 'Unread',
        'rs-status--hidden': this.variant === 'hidden',
        'rs-status--in-progress': this.variant === 'in-progress',
        'rs-status--promo-scheduled': this.variant === 'promo-scheduled',
      };
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.rs-status {
  display: flex;
  align-items: center;
  justify-content: center;
  .rs-icon {
    font-size: 1.1rem;
  }

  &__text {
    margin-left: 0.5rem;
    font-weight: 600;
    font-size: 0.875rem;
    color: $global-text-color;
  }
  &--requested {
    .rs-icon {
      color: $info;
    }
  }
  &--declined {
    .rs-icon {
      color: $error;
    }
  }
  &--resolved {
    .rs-icon {
      color: $success;
    }
  }
  &--Unread {
    .rs-icon {
      margin: 1rem;
      color: $success;
    }
  }
  &--Read {
    .rs-icon {
      margin: 1rem;
      color: $tertiary-gray;
    }
  }
  &--scheduled {
    .rs-icon {
      color: $secondary-color;
    }
  }
  &--in-progress {
    .rs-icon {
      color: $success;
    }
  }
  &--promo-scheduled {
    .rs-icon {
      color: $secondary-color;
    }
  }
  &--unsubscribed {
    .rs-icon {
      color: $error;
    }
  }
  &--spam {
    .rs-icon {
      color: $error;
    }
  }
  &--unlisted {
    .rs-icon {
      color: $error;
    }
  }
  &--sent {
    .rs-icon {
      color: $success;
    }
  }
  &--expired {
    .rs-icon {
      color: $warning;
    }
  }
  &--waiting {
    .rs-icon {
      color: $warning;
    }
  }
  &--contacted {
    .rs-icon {
      color: $info;
    }
  }
  &--converted {
    .rs-icon {
      color: $success;
    }
  }
  &--removed {
    .rs-icon {
      color: $tertiary-gray;
    }
  }
  &--spam {
    .rs-icon {
      color: $warning;
    }
  }
  &--enabled {
    .rs-icon {
      color: $success;
    }
  }
  &--disabled {
    .rs-icon {
      color: $error;
    }
  }
  &--hidden {
    .rs-icon {
      color: $tertiary-gray;
    }
  }
}
</style>
